import React, { useMemo } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';

import Icon from 'components/icon';
import Loader from 'components/loader';

import { getLocalization, isSearchValueValid } from './utils';
import { useSearch } from './hooks';
import { Form, Menu, SearchButton, SearchSuggestions } from './components';
import { SUGGESTIONS_CONTAINER_ID } from './constants';

const Search = () => {
    const data = useMemo(() => {
        if (!window.config || !window.config.search) {
            return null;
        }

        return window.config.search;
    }, []);

    const {
        api,
        serpUrl,
        siteName,
        culture,
        categoryField,
        searchField,
        activeMenu,
        suggestionsSettings
    } = data || {};

    const { cards, links } = activeMenu || {};
    const { maxLength, minLength } = suggestionsSettings || {};

    const menuIsNotEmpty = useMemo(
        () => (cards && !!cards.length) || (links && !!links.length),
        []
    );

    const {
        handleFocus,
        handleBlur,
        handleChange,
        handleSelectChange,
        handleSubmit,
        handleCloseButtonClick,
        handleSearchButtonClick,
        handleCloseSearchButtonClick,
        handleClickOutside,
        isFocused,
        categoryValue,
        searchValue,
        suggestions,
        composedSerpUrl,
        menuIsOpen,
        inputRef,
        resultsAmount,
        isLoading
    } = useSearch({
        api,
        serpUrl,
        categoryInitialValue: categoryField ? categoryField.value : null,
        searchInitialValue: searchField ? searchField.value : '',
        menuIsNotEmpty,
        suggestionsSettings: suggestionsSettings || {},
        siteName,
        culture
    });

    const isQueryValid = useMemo(
        () =>
            isSearchValueValid({
                value: searchValue,
                minLength,
                maxLength
            }) && isFocused,
        [searchValue, isFocused]
    );

    if (!data) {
        return null;
    }

    return (
        <OutsideClickHandler onOutsideClick={handleClickOutside}>
            <div className="c-search">
                <Form
                    serpUrl={serpUrl}
                    handleSubmit={handleSubmit}
                    categoryField={categoryField}
                    searchField={searchField}
                    handleSelectChange={handleSelectChange}
                    handleFocus={handleFocus}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    selectValue={categoryValue}
                    inputMaxLength={maxLength}
                    inputValue={searchValue}
                    inputRef={inputRef}
                    buttonTitle={getLocalization('search/submit')}
                    isFocused={isFocused}
                    isQueryValid={isQueryValid}
                />
                <div
                    className={`c-search__dropdown ${
                        menuIsOpen || suggestions ? 'is-visible' : ''
                    }`}
                >
                    <div className="c-search__dropdown-overlay" />
                    <div
                        className={`c-search__dropdown-inner ${
                            menuIsOpen ? 'with-padding-bottom' : ''
                        }`}
                        id={SUGGESTIONS_CONTAINER_ID}
                    >
                        {isLoading && (
                            <div className="search-loader-container">
                                <Loader loaderClassName="search-loader" />
                            </div>
                        )}
                        {menuIsOpen && (
                            <div className="wrapper">
                                <Menu cards={cards} links={links} />
                            </div>
                        )}
                        {suggestions && (
                            <React.Fragment>
                                <div className="wrapper">
                                    <SearchSuggestions
                                        suggestions={suggestions}
                                    />
                                </div>
                                <div className="c-search__suggestions-button-container">
                                    <a
                                        className={`button button--secondary c-search__suggestions-button ${
                                            !suggestions || !suggestions.length
                                                ? 'is-disabled'
                                                : ''
                                        }`}
                                        href={composedSerpUrl}
                                    >
                                        {getLocalization(
                                            'search/suggestions/results/button'
                                        )}{' '}
                                        {!!resultsAmount &&
                                            `(${resultsAmount})`}
                                    </a>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </div>

                {(menuIsOpen || suggestions) && (
                    <button
                        className="c-search__dropdown-close"
                        onClick={handleCloseButtonClick}
                        type="button"
                    >
                        {getLocalization('search/dropdown/close')}
                        <Icon glyph="cross" />
                    </button>
                )}
                <button
                    className="c-search__search-close"
                    onClick={handleCloseSearchButtonClick}
                    type="button"
                >
                    {getLocalization('search/dropdown/close')}
                    <Icon glyph="cross" />
                </button>
                <SearchButton onClick={handleSearchButtonClick} />
            </div>
        </OutsideClickHandler>
    );
};

export default Search;
